import { useContext, useMemo } from 'react';

import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

import { SECONDS_IN_DAY } from '@cast/constants';
import { useCluster } from '@cast/shared-cluster-context-data-access';
import { WorkloadDetailsContext } from '@cast/split-me/features/cluster/workload-optimization';
import { useWorkloadCostReportQuery } from '@cast/split-me/hooks';
import { attachTimeTicks } from '@cast/split-me/utils';
import { PriceType } from '@cast/shared-types';
import { getPriceMultiplier, toBig, toNumOrZero } from '@cast/utils';
import meanBy from 'lodash/meanBy';
import { WorkloadCostMetric } from '@cast/types';

import { useInventoryWorkloadsTimeRangeSelection } from '../../../../_hooks/useInventoryWorkloadsTimeRangeSelection';

dayjs.extend(utc);
dayjs.extend(timezone);

export type WorkloadResourceCostRate = 'hourly' | 'daily' | 'monthly';

export type WorkloadResourceCostChartPoint = {
  timestamp: string;
  chartTimestampUnix: number;
  normalized: number | null;
  onDemand: number | null;
  fallback: number | null;
  spot: number | null;
  forecast: boolean;
};

const HOURS_IN_DAY = 24;
const FORECAST_WINDOW_DAYS = 7;

const rateToPriceType: Record<WorkloadResourceCostRate, PriceType> = {
  hourly: PriceType.HOURLY,
  daily: PriceType.DAILY,
  monthly: PriceType.MONTHLY,
};

export type ResourceCostFieldConfig = {
  onDemandCost: keyof WorkloadCostMetric;
  spotCost: keyof WorkloadCostMetric;
  fallbackCost: keyof WorkloadCostMetric;
  onDemandCount: keyof WorkloadCostMetric;
  spotCount: keyof WorkloadCostMetric;
  fallbackCount: keyof WorkloadCostMetric;
  /** Whether the API returns hourly cost for this resource. Storage returns daily cost. */
  isHourly: boolean;
  /** Whether to show on-demand / fallback / spot offering breakdown in the tooltip. */
  hasOfferingBreakdown: boolean;
};

type AggregatedPoint = {
  timestamp: string;
  onDemandCost: number;
  onDemandCount: number;
  spotCost: number;
  spotCount: number;
  fallbackCost: number;
  fallbackCount: number;
  /**
   * Forecast-only internal values used to compute the normalized cost.
   * Cost-report keeps fallback chart values at 0 for forecasts but still
   * includes the forecast fallback count in the total count used for
   * normalization.
   */
  forecastFallbackCost?: number;
  forecastFallbackCount?: number;
  forecast: boolean;
};

type AggregatedByDate = Record<string, AggregatedPoint>;

type BuildChartDataOptions = {
  aggregatedByDate: AggregatedByDate;
  range: { from: string; to: string };
  rate: WorkloadResourceCostRate;
  hasOfferingBreakdown: boolean;
};

const getCostValue = (
  metric: WorkloadCostMetric,
  field: keyof WorkloadCostMetric,
  isHourly: boolean
): number => {
  const value = toNumOrZero(metric[field]);

  return isHourly ? value : value / HOURS_IN_DAY;
};

const aggregateMetrics = (
  metrics: WorkloadCostMetric[] | undefined,
  fields: ResourceCostFieldConfig
): AggregatedByDate => {
  const byTimestamp: Record<string, AggregatedPoint> = {};

  if (!metrics) {
    return byTimestamp;
  }

  for (const metric of metrics) {
    if (!metric.timestamp) {
      continue;
    }

    const timestamp = dayjs.utc(metric.timestamp).toISOString();
    const dateKey = dayjs.utc(timestamp).format('YYYY-MM-DD');

    const onDemandCost = getCostValue(
      metric,
      fields.onDemandCost,
      fields.isHourly
    );
    const spotCost = getCostValue(metric, fields.spotCost, fields.isHourly);
    const fallbackCost = getCostValue(
      metric,
      fields.fallbackCost,
      fields.isHourly
    );

    const onDemandCount = toNumOrZero(metric[fields.onDemandCount]);
    const spotCount = toNumOrZero(metric[fields.spotCount]);
    const fallbackCount = toNumOrZero(metric[fields.fallbackCount]);

    if (dateKey in byTimestamp) {
      byTimestamp[dateKey].onDemandCost += onDemandCost;
      byTimestamp[dateKey].onDemandCount += onDemandCount;
      byTimestamp[dateKey].spotCost += spotCost;
      byTimestamp[dateKey].spotCount += spotCount;
      byTimestamp[dateKey].fallbackCost += fallbackCost;
      byTimestamp[dateKey].fallbackCount += fallbackCount;
    } else {
      byTimestamp[dateKey] = {
        timestamp,
        onDemandCost,
        onDemandCount,
        spotCost,
        spotCount,
        fallbackCost,
        fallbackCount,
        forecast: false,
      };
    }
  }

  return byTimestamp;
};

const mean = (values: number[]): number => {
  if (values.length === 0) {
    return 0;
  }

  return values.reduce((sum, value) => sum + value, 0) / values.length;
};

const generateForecast = (
  actualByDate: AggregatedByDate,
  range: { from: string; to: string },
  timezone: string
): AggregatedByDate => {
  const actualDates = Object.keys(actualByDate).sort();

  if (actualDates.length === 0) {
    return actualByDate;
  }

  const firstFutureDate = dayjs().tz(timezone).startOf('day').add(1, 'day');
  const lastDate = dayjs.utc(range.to);

  if (!lastDate.isAfter(firstFutureDate)) {
    return actualByDate;
  }

  const result: AggregatedByDate = { ...actualByDate };

  let current = firstFutureDate.clone();

  while (!current.isAfter(lastDate)) {
    const dateKey = current.format('YYYY-MM-DD');
    const window = actualDates.slice(-FORECAST_WINDOW_DAYS);

    const onDemandCost = mean(window.map((date) => result[date].onDemandCost));
    const onDemandCount = mean(
      window.map((date) => result[date].onDemandCount)
    );
    const fallbackCost = mean(window.map((date) => result[date].fallbackCost));
    const fallbackCount = mean(
      window.map((date) => result[date].fallbackCount)
    );

    // Cost-report adds half of fallback to spot forecast.
    const spotCost =
      mean(window.map((date) => result[date].spotCost)) + fallbackCost / 2;
    const spotCount =
      mean(window.map((date) => result[date].spotCount)) + fallbackCount;

    result[dateKey] = {
      timestamp: current.toISOString(),
      onDemandCost,
      onDemandCount,
      spotCost,
      spotCount,
      // Cost-report stores fallback values as 0 for forecasts but still uses
      // the forecast fallback cost/count when computing the normalized value.
      fallbackCost: 0,
      fallbackCount: 0,
      forecastFallbackCost: fallbackCost,
      forecastFallbackCount: fallbackCount,
      forecast: true,
    };

    actualDates.push(dateKey);
    current = current.add(1, 'day');
  }

  return result;
};

const buildChartData = ({
  aggregatedByDate,
  range,
  rate,
  hasOfferingBreakdown,
}: BuildChartDataOptions): WorkloadResourceCostChartPoint[] => {
  const multiplier = getPriceMultiplier(
    rateToPriceType[rate],
    PriceType.HOURLY
  );

  const from = dayjs.utc(range.from);
  const to = dayjs.utc(range.to);
  const points: WorkloadResourceCostChartPoint[] = [];

  let current = from.clone();

  while (!current.isAfter(to)) {
    const timestamp = current.toISOString();
    const dateKey = current.format('YYYY-MM-DD');
    const aggregated = aggregatedByDate[dateKey];

    if (aggregated) {
      const effectiveFallbackCost =
        aggregated.forecastFallbackCost ?? aggregated.fallbackCost;
      const effectiveFallbackCount =
        aggregated.forecastFallbackCount ?? aggregated.fallbackCount;

      const totalCost =
        aggregated.onDemandCost + effectiveFallbackCost + aggregated.spotCost;
      const totalCount =
        aggregated.onDemandCount +
        effectiveFallbackCount +
        aggregated.spotCount;

      const normalized =
        totalCount > 0
          ? toBig(totalCost).div(totalCount).mul(multiplier).toNumber()
          : 0;
      const onDemand = hasOfferingBreakdown
        ? aggregated.onDemandCount > 0
          ? toBig(aggregated.onDemandCost)
              .div(aggregated.onDemandCount)
              .mul(multiplier)
              .toNumber()
          : 0
        : null;
      // Cost-report normalized cost sets fallback to 0 for forecast points.
      const fallback =
        hasOfferingBreakdown && !aggregated.forecast
          ? aggregated.fallbackCount > 0
            ? toBig(aggregated.fallbackCost)
                .div(aggregated.fallbackCount)
                .mul(multiplier)
                .toNumber()
            : 0
          : hasOfferingBreakdown
          ? 0
          : null;
      const spot = hasOfferingBreakdown
        ? aggregated.spotCount > 0
          ? toBig(aggregated.spotCost)
              .div(aggregated.spotCount)
              .mul(multiplier)
              .toNumber()
          : 0
        : null;

      points.push({
        timestamp,
        chartTimestampUnix: 0,
        normalized,
        onDemand,
        fallback,
        spot,
        forecast: aggregated.forecast,
      });
    } else {
      points.push({
        timestamp,
        chartTimestampUnix: 0,
        normalized: null,
        onDemand: null,
        fallback: null,
        spot: null,
        forecast: false,
      });
    }

    current = current.add(1, 'day');
  }

  return attachTimeTicks(points);
};

export const useWorkloadResourceCostChartData = (
  rate: WorkloadResourceCostRate = 'daily',
  fields: ResourceCostFieldConfig
) => {
  const { workload } = useContext(WorkloadDetailsContext);
  const { cluster } = useCluster();
  const { getRange, timeZone, key } = useInventoryWorkloadsTimeRangeSelection();

  const range = useMemo(() => getRange(), [key]);
  const isRangeSupported = useMemo(
    () => dayjs(range.to).diff(dayjs(range.from), 'day', true) >= 2,
    [range]
  );

  // useWorkloadCostReportQuery assumes daily aggregation and adjusts timestamps
  // by subtracting one day; keep the daily step to stay aligned with that logic.
  const stepSeconds = SECONDS_IN_DAY;

  const {
    workload: report,
    isLoading,
    error,
    refetch,
  } = useWorkloadCostReportQuery({
    clusterId: cluster.id,
    namespace: workload?.namespace ?? '',
    workloadType: workload?.kind ?? '',
    workloadName: workload?.name ?? '',
    range: [dayjs(range.from), dayjs(range.to)],
    useListingPrices: false,
    stepSeconds,
    includeIdleCosts: false,
    enabled: !!cluster.id && !!workload && isRangeSupported,
  });

  const aggregatedByDate = useMemo(
    () => aggregateMetrics(report?.costMetrics as WorkloadCostMetric[], fields),
    [report, fields]
  );

  const data = useMemo(
    () =>
      buildChartData({
        aggregatedByDate: generateForecast(aggregatedByDate, range, timeZone),
        range: { from: range.from, to: range.to },
        rate,
        hasOfferingBreakdown: fields.hasOfferingBreakdown,
      }),
    [aggregatedByDate, range, rate, fields.hasOfferingBreakdown, timeZone]
  );

  const averageCost = useMemo(() => {
    const validPoints = data.filter(
      (
        point
      ): point is WorkloadResourceCostChartPoint & { normalized: number } =>
        point.normalized !== null
    );

    if (validPoints.length === 0) {
      return 0;
    }

    return meanBy(validPoints, 'normalized');
  }, [data]);

  return {
    data,
    averageCost,
    isLoading,
    error: error as Error | null,
    refetch,
    timeZone,
    stepSeconds,
    range,
    isRangeSupported,
  };
};
